import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {
    add,
    cloneRecord,
    deleteRecord,
    update
} from '../services/ProductsService';
import {createProductQueryOptions} from '../query-options/createProductQueryOptions';
import {Product} from '../types';

export const useProduct = (productId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const productQuery = useQuery({
                                      ...createProductQueryOptions(productId!),
                                      enabled: !!productId
                                  });

    // CREATE MUTATION
    const createMutation = useMutation({
                                           mutationFn: (values: Product) => {
                                               const {
                                                   lineItems,
                                                   created,
                                                   createdByUsername,
                                                   createdByUser,
                                                   updated,
                                                   ...rest
                                               } = values;
                                               return add(rest);
                                           },
                                           onSuccess: () => {
                                               queryClient.invalidateQueries({queryKey: ['products']});
                                               queryClient.invalidateQueries({queryKey: ['all-products']});
                                           }
                                       });

    // UPDATE MUTATION
    const updateMutation = useMutation({
                                           mutationFn: (values: Product) => {
                                               if (!productId) throw new Error('productId is required parameter');
                                               const {
                                                   lineItems,
                                                   created,
                                                   createdByUsername,
                                                   createdByUser,
                                                   updated,
                                                   ...rest
                                               } = values;
                                               return update(productId, rest);
                                           },
                                           onSuccess: () => {
                                               queryClient.invalidateQueries({queryKey: ['products']});
                                               queryClient.invalidateQueries({queryKey: ['all-products']});
                                               queryClient.invalidateQueries({queryKey: ['product', productId]});
                                           }
                                       });

    // CLONE MUTATION
    const cloneMutation = useMutation({
                                          mutationFn: () => {
                                              if (!productId) throw new Error('productId is required parameter');
                                              return cloneRecord(productId);
                                          },
                                          onSuccess: () => {
                                              queryClient.invalidateQueries({queryKey: ['products']});
                                              queryClient.invalidateQueries({queryKey: ['all-products']});
                                          }
                                      });

    // DELETE MUTATION
    const deleteMutation = useMutation({
                                           mutationFn: () => {
                                               if (!productId) throw new Error('productId is required parameter');
                                               return deleteRecord(productId);
                                           },
                                           onSuccess: () => {
                                               queryClient.invalidateQueries({queryKey: ['products']});
                                               queryClient.invalidateQueries({queryKey: ['all-products']});
                                           }
                                       });

    return {
        record: productQuery.data,
        loading: productQuery.isLoading,
        fetchRecord: productQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        cloneRecord: cloneMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        cloneMutation,
        deleteMutation
    };
};
